import { useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  Flex,
  Heading,
  Label,
  Paragraph,
  Switch,
  toast,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useChannelsConnected } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { actions as profileSidebarActions } from '~publish/legacy/profile-sidebar/reducer'
import { selectCurrentProfile } from '~publish/legacy/profile-sidebar/selectors'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'

import commonStyles from '../../GeneralSettings.module.css'

export const UpdateTrendingTopicSuggestionsVisibility = graphql(/* GraphQL */ `
  mutation UpdateTrendingTopicSuggestionsVisibility(
    $input: UpdateTrendingTopicSuggestionsVisibilityInput!
  ) {
    updateTrendingTopicSuggestionsVisibility(input: $input) {
      __typename
      ... on UpdateTrendingTopicSuggestionsVisibilitySuccess {
        channel {
          id
          showTrendingTopicSuggestions
        }
      }
      ... on NotFoundError {
        message
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

const TrendingTopicsSetting = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const profile = useAppSelector(selectCurrentProfile)
  const channels = useChannelsConnected()
  const [isLoading, setLoading] = useState(false)
  const commonTrackingProperties = useCommonTrackingProperties()

  const [updateTrendingTopicSuggestionsVisibility] = useMutation(
    UpdateTrendingTopicSuggestionsVisibility,
  )

  if (!profile) return <></>

  // Get the value from Apollo cache (channels from GetAccount query)
  // This ensures the UI updates when the mutation updates the cache
  const channel = channels.find((ch) => ch.id === profile.id)

  // Default to true if channel is not found
  const showTrendingTopicSuggestions =
    channel?.showTrendingTopicSuggestions ?? true
  const profileId = profile.id

  const onToggleChange = async (checked: boolean): Promise<void> => {
    setLoading(true)

    try {
      const result = await updateTrendingTopicSuggestionsVisibility({
        variables: {
          input: {
            channelId: profileId,
            showTrendingTopicSuggestions: checked,
          },
        },
      })

      const response = result.data?.updateTrendingTopicSuggestionsVisibility

      if (
        response?.__typename ===
        'UpdateTrendingTopicSuggestionsVisibilitySuccess'
      ) {
        // Track the toggle event
        BufferTracker.trendingTopicsSuggestionsToggled({
          ...commonTrackingProperties,
          product: 'publish',
          clientName: 'publishWeb',
          cta: 'publish-settings-trendingTopicsSuggestions-toggle',
          channel: channel?.service ?? '',
          channelId: profileId,
          channelUsername: channel?.name,
          state: checked ? 'enabled' : 'disabled',
          previousState: showTrendingTopicSuggestions ? 'enabled' : 'disabled',
        })

        // Update Redux state so the composer picks up the new value
        dispatch(
          profileSidebarActions.updateTrendingTopicSuggestionsVisibility({
            profileId,
            showTrendingTopicSuggestions: checked,
          }),
        )
        toast.success(
          `Trending topic suggestions ${checked ? 'enabled' : 'disabled'}`,
        )
      } else if (
        response?.__typename === 'NotFoundError' ||
        response?.__typename === 'InvalidInputError'
      ) {
        toast.error(response.message)
      }
    } catch {
      toast.error(
        `Sorry! Something went wrong while updating trending topic suggestions. Would you be up for trying again?`,
      )
    }

    setLoading(false)
  }

  return (
    <Flex
      justify="between"
      align="center"
      className={commonStyles.fullWidth}
      gap="xl"
    >
      <Flex direction="column">
        <Heading size="small" as="h3">
          Trending Topics Suggestions
        </Heading>
        <Paragraph className={commonStyles.settingsParagraph}>
          Show trending topic suggestions in the composer. When off, you can
          still view trends anytime by clicking the trends button within the
          Topic field.
        </Paragraph>
      </Flex>
      <Flex
        className={commonStyles.actionsWrapper}
        justify="end"
        gap="xs"
        align="center"
      >
        <Label>{showTrendingTopicSuggestions ? 'On' : 'Off'}</Label>
        <Switch
          disabled={isLoading}
          onCheckedChange={onToggleChange}
          checked={showTrendingTopicSuggestions}
          data-testid="trending-topics-suggestions-toggle"
        />
      </Flex>
    </Flex>
  )
}

export { TrendingTopicsSetting }
